/**
 * Cercle trigonométrique — calculs PURS.
 *
 * Les angles « remarquables » sont les multiples de π/6 et de π/4 (16 par
 * tour). On les repère en pas de 15° (π/12) : un angle remarquable est un
 * multiple de 15° qui est aussi multiple de 30° ou de 45°. Leurs cosinus,
 * sinus et tangentes sont donnés EXACTEMENT (√2/2, pas 0,7071), avec les bouts
 * à poser dans la feuille de brouillon.
 */

export const STEP = Math.PI / 12; // 15°
const TURN = 24; // pas de 15° par tour

/** Valeur exacte : écriture KaTeX, texte simple, bouts de la feuille, valeur. */
export interface ExactValue {
  tex: string;
  plain: string;
  atoms: string[];
  value: number;
}

const ZERO: ExactValue = { tex: '0', plain: '0', atoms: ['0'], value: 0 };
const ONE: ExactValue = { tex: '1', plain: '1', atoms: ['1'], value: 1 };
const HALF: ExactValue = {
  tex: '\\frac{1}{2}',
  plain: '1/2',
  atoms: ['1', '÷', '2'],
  value: 0.5,
};
const R2_2: ExactValue = {
  tex: '\\frac{\\sqrt{2}}{2}',
  plain: '√2/2',
  atoms: ['√', '2', '÷', '2'],
  value: Math.SQRT2 / 2,
};
const R3_2: ExactValue = {
  tex: '\\frac{\\sqrt{3}}{2}',
  plain: '√3/2',
  atoms: ['√', '3', '÷', '2'],
  value: Math.sqrt(3) / 2,
};
const R3_3: ExactValue = {
  tex: '\\frac{\\sqrt{3}}{3}',
  plain: '√3/3',
  atoms: ['√', '3', '÷', '3'],
  value: Math.sqrt(3) / 3,
};
const R3: ExactValue = { tex: '\\sqrt{3}', plain: '√3', atoms: ['√', '3'], value: Math.sqrt(3) };

/** |cos| selon l'angle de référence (0, 30, 45, 60, 90°) */
const COS_ABS: Record<number, ExactValue> = { 0: ONE, 30: R3_2, 45: R2_2, 60: HALF, 90: ZERO };
const SIN_ABS: Record<number, ExactValue> = { 0: ZERO, 30: HALF, 45: R2_2, 60: R3_2, 90: ONE };
const TAN_ABS: Record<number, ExactValue | null> = { 0: ZERO, 30: R3_3, 45: ONE, 60: R3, 90: null };

function negate(v: ExactValue): ExactValue {
  if (v.value === 0) return v;
  return { tex: `-${v.tex}`, plain: `−${v.plain}`, atoms: ['−', ...v.atoms], value: -v.value };
}

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Le pas k (en 15°) est-il un angle remarquable ? */
export function isRemarkableStep(k: number): boolean {
  const r = mod(k, TURN);
  return r % 2 === 0 || r % 3 === 0;
}

/** Les 16 pas remarquables d'un tour, de 0 à 22. */
export const REMARKABLE_STEPS: readonly number[] = Array.from({ length: TURN }, (_, k) => k).filter(
  isRemarkableStep,
);

export interface TrigExact {
  cos: ExactValue;
  sin: ExactValue;
  /** null : la tangente n'existe pas (cos = 0) */
  tan: ExactValue | null;
}

/** Valeurs exactes d'un angle remarquable donné en pas de 15°. */
export function exactValues(k: number): TrigExact | null {
  if (!isRemarkableStep(k)) return null;
  const deg = mod(k, TURN) * 15;
  const r = deg % 180;
  const ref = r <= 90 ? r : 180 - r;
  const cosSign = deg === 90 || deg === 270 ? 0 : deg < 90 || deg > 270 ? 1 : -1;
  const sinSign = deg === 0 || deg === 180 ? 0 : deg < 180 ? 1 : -1;
  const cos = cosSign < 0 ? negate(COS_ABS[ref]!) : COS_ABS[ref]!;
  const sin = sinSign < 0 ? negate(SIN_ABS[ref]!) : SIN_ABS[ref]!;
  const tanAbs = TAN_ABS[ref] ?? null;
  const tan = tanAbs === null ? null : cosSign * sinSign < 0 ? negate(tanAbs) : tanAbs;
  return { cos, sin, tan };
}

/**
 * Aimant : l'angle le plus proche parmi les remarquables, s'il est à moins de
 * `tolDeg` degrés. Renvoie le pas k (sans réduire au tour : 25 = 375°).
 */
export function snapStep(theta: number, tolDeg = 4): number | null {
  const base = Math.round(theta / STEP);
  let best: number | null = null;
  for (let k = base - 2; k <= base + 2; k++) {
    if (!isRemarkableStep(k)) continue;
    if (best === null || Math.abs(k * STEP - theta) < Math.abs(best * STEP - theta)) best = k;
  }
  if (best === null) return null;
  return Math.abs(best * STEP - theta) <= (tolDeg * Math.PI) / 180 ? best : null;
}

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** Mesure en radians d'un pas k : écriture KaTeX et texte (« 5π/6 », « −π »). */
export function radianLabel(k: number): { tex: string; plain: string } {
  if (k === 0) return { tex: '0', plain: '0' };
  const g = gcd(k, 12);
  const p = k / g;
  const q = 12 / g;
  const sign = p < 0 ? '-' : '';
  const plainSign = p < 0 ? '−' : '';
  const n = Math.abs(p);
  const num = n === 1 ? '\\pi' : `${n}\\pi`;
  const numPlain = n === 1 ? 'π' : `${n}π`;
  if (q === 1) return { tex: `${sign}${num}`, plain: `${plainSign}${numPlain}` };
  return { tex: `${sign}\\frac{${num}}{${q}}`, plain: `${plainSign}${numPlain}/${q}` };
}

/** Mesure principale (dans ]−π ; π]) d'un pas k. */
export function principalStep(k: number): number {
  const r = mod(k, TURN);
  return r > 12 ? r - TURN : r;
}

/** Nombre à virgule, 3 décimales au plus (« 0,866 », « −1 »). */
export function formatApprox(v: number, digits = 3): string {
  const r = Number(v.toFixed(digits));
  const s = (Object.is(r, -0) ? 0 : r).toString().replace('.', ',');
  return s.replace('-', '−');
}

/** Degrés d'un angle quelconque, arrondis au dixième (« 37,4° »). */
export function degreeLabel(theta: number): string {
  return `${formatApprox((theta * 180) / Math.PI, 1)}°`;
}

/**
 * Angle « déroulé » : on suit le doigt tour après tour. `prev` est l'angle
 * déroulé précédent, `raw` l'angle lu sur le cercle (atan2) : on ajoute le plus
 * petit écart, pour qu'un tour complet donne 2π et pas 0.
 */
export function unwrapAngle(prev: number, raw: number): number {
  const twoPi = 2 * Math.PI;
  let d = (raw - prev) % twoPi;
  if (d > Math.PI) d -= twoPi;
  if (d <= -Math.PI) d += twoPi;
  return prev + d;
}

export interface AssociatedAngle {
  id: 'neg' | 'pi-minus' | 'pi-plus' | 'half-pi-minus';
  /** écriture de l'angle (« −x », « π − x ») */
  label: string;
  /** angle associé à x */
  of: (x: number) => number;
  cosRule: string;
  sinRule: string;
}

export const ASSOCIATED_ANGLES: readonly AssociatedAngle[] = [
  {
    id: 'neg',
    label: '−x',
    of: (x) => -x,
    cosRule: 'cos(−x) = cos x',
    sinRule: 'sin(−x) = −sin x',
  },
  {
    id: 'pi-minus',
    label: 'π − x',
    of: (x) => Math.PI - x,
    cosRule: 'cos(π − x) = −cos x',
    sinRule: 'sin(π − x) = sin x',
  },
  {
    id: 'pi-plus',
    label: 'π + x',
    of: (x) => Math.PI + x,
    cosRule: 'cos(π + x) = −cos x',
    sinRule: 'sin(π + x) = −sin x',
  },
  {
    id: 'half-pi-minus',
    label: 'π/2 − x',
    of: (x) => Math.PI / 2 - x,
    cosRule: 'cos(π/2 − x) = sin x',
    sinRule: 'sin(π/2 − x) = cos x',
  },
];

// ── Défi : placer un angle ─────────────────────────────────────────────────

/** Un angle remarquable à placer, en mesure principale, différent du précédent. */
export function pickTarget(rng: () => number, previous: number | null = null): number {
  const pool = REMARKABLE_STEPS.map(principalStep).filter((k) => k !== previous);
  return pool[Math.min(pool.length - 1, Math.floor(rng() * pool.length))]!;
}

/** L'angle posé (pas k, éventuellement après plusieurs tours) est-il la cible ? */
export function isOnTarget(k: number | null, target: number): boolean {
  return k !== null && mod(k - target, TURN) === 0;
}

export const CHALLENGE_ROUNDS = 10;
